import React, { useRef, useEffect, useState } from 'react';
import './GameDemo.css';

const GameDemo = () => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('start');
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('msaqeel-highscore') || '0');
  });
  const gameLoopRef = useRef(null);
  const gameDataRef = useRef({
    player: { x: 0, y: 0, width: 40, height: 40, speed: 5, dx: 0, dy: 0 },
    stars: [],
    obstacles: [],
    particles: [],
    keys: {}
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const wrapper = canvas.parentElement;

    const resizeCanvas = () => {
      canvas.width = wrapper.offsetWidth;
      canvas.height = 500;
      gameDataRef.current.player.x = canvas.width / 2;
      gameDataRef.current.player.y = canvas.height - 80;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Keyboard controls
    const handleKeyDown = (e) => {
      gameDataRef.current.keys[e.key] = true;
      if (e.key === ' ' && gameState === 'start') {
        e.preventDefault();
        startGame();
      }
    };

    const handleKeyUp = (e) => {
      gameDataRef.current.keys[e.key] = false;
    };

    // Touch controls
    let touchStartX = 0;
    let touchStartY = 0;

    const handleTouchStart = (e) => {
      if (gameState === 'start') {
        startGame();
        return;
      }
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      if (gameState !== 'playing') return;
      e.preventDefault();
      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;
      const deltaX = touchX - touchStartX;
      const deltaY = touchY - touchStartY;

      gameDataRef.current.player.x += deltaX * 0.5;
      gameDataRef.current.player.y += deltaY * 0.5;

      touchStartX = touchX;
      touchStartY = touchY;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });

    // Game loop
    const gameLoop = () => {
      drawBackground(ctx, canvas);

      if (gameState === 'playing') {
        updatePlayer(canvas);
        updateStars(canvas);
        updateObstacles(canvas);
        updateParticles();

        drawStars(ctx);
        drawObstacles(ctx);
        drawParticles(ctx);
        drawPlayer(ctx);
      } else {
        drawPlayer(ctx);
      }

      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoop();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [gameState]);

  const createStar = (canvas) => ({
    x: Math.random() * canvas.width,
    y: -20,
    width: 30,
    height: 30,
    speed: 2 + Math.random() * 2,
    collected: false
  });

  const createObstacle = (canvas) => ({
    x: Math.random() * canvas.width,
    y: -20,
    width: 35,
    height: 35,
    speed: 3 + Math.random() * 2
  });

  const createParticle = (x, y, color) => {
    const particles = [];
    for (let i = 0; i < 10; i++) {
      particles.push({
        x: x,
        y: y,
        dx: (Math.random() - 0.5) * 5,
        dy: (Math.random() - 0.5) * 5,
        radius: Math.random() * 3 + 2,
        color: color,
        life: 1
      });
    }
    return particles;
  };

  const updatePlayer = (canvas) => {
    const { player, keys } = gameDataRef.current;

    if (keys['ArrowRight'] || keys['d']) {
      player.dx = player.speed;
    } else if (keys['ArrowLeft'] || keys['a']) {
      player.dx = -player.speed;
    } else {
      player.dx = 0;
    }

    if (keys['ArrowUp'] || keys['w']) {
      player.dy = -player.speed;
    } else if (keys['ArrowDown'] || keys['s']) {
      player.dy = player.speed;
    } else {
      player.dy = 0;
    }

    player.x += player.dx;
    player.y += player.dy;

    if (player.x < 0) player.x = 0;
    if (player.x + player.width > canvas.width) player.x = canvas.width - player.width;
    if (player.y < 0) player.y = 0;
    if (player.y + player.height > canvas.height) player.y = canvas.height - player.height;
  };

  const updateStars = (canvas) => {
    const { player, stars, particles } = gameDataRef.current;

    if (Math.random() < 0.02) {
      stars.push(createStar(canvas));
    }

    for (let i = stars.length - 1; i >= 0; i--) {
      const star = stars[i];
      star.y += star.speed;

      if (!star.collected &&
          player.x < star.x + star.width &&
          player.x + player.width > star.x &&
          player.y < star.y + star.height &&
          player.y + player.height > star.y) {
        star.collected = true;
        setScore(prev => prev + 10);
        particles.push(...createParticle(star.x + star.width / 2, star.y + star.height / 2, '#fbbf24'));
      }

      if (star.y > canvas.height || star.collected) {
        stars.splice(i, 1);
      }
    }
  };

  const updateObstacles = (canvas) => {
    const { player, obstacles } = gameDataRef.current;

    if (Math.random() < 0.015) {
      obstacles.push(createObstacle(canvas));
    }

    for (let i = obstacles.length - 1; i >= 0; i--) {
      const obstacle = obstacles[i];
      obstacle.y += obstacle.speed;

      if (player.x < obstacle.x + obstacle.width &&
          player.x + player.width > obstacle.x &&
          player.y < obstacle.y + obstacle.height &&
          player.y + player.height > obstacle.y) {
        endGame();
      }

      if (obstacle.y > canvas.height) {
        obstacles.splice(i, 1);
      }
    }
  };

  const updateParticles = () => {
    const { particles } = gameDataRef.current;

    for (let i = particles.length - 1; i >= 0; i--) {
      const particle = particles[i];
      particle.x += particle.dx;
      particle.y += particle.dy;
      particle.dy += 0.1;
      particle.life -= 0.02;

      if (particle.life <= 0) {
        particles.splice(i, 1);
      }
    }
  };

  const drawBackground = (ctx, canvas) => {
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#1e1e2e');
    gradient.addColorStop(1, '#2d2d44');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    for (let i = 0; i < 50; i++) {
      const x = (i * 123) % canvas.width;
      const y = (i * 456 + Date.now() / 20) % canvas.height;
      ctx.beginPath();
      ctx.arc(x, y, 1, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const drawPlayer = (ctx) => {
    const { player } = gameDataRef.current;

    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 5;

    const gradient = ctx.createLinearGradient(player.x, player.y, player.x, player.y + player.height);
    gradient.addColorStop(0, '#667eea');
    gradient.addColorStop(1, '#764ba2');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(player.x + player.width / 2, player.y + player.height / 2, player.width / 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(player.x + player.width / 2 - 8, player.y + player.height / 2 - 5, 5, 0, Math.PI * 2);
    ctx.arc(player.x + player.width / 2 + 8, player.y + player.height / 2 - 5, 5, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.arc(player.x + player.width / 2 - 8, player.y + player.height / 2 - 5, 2, 0, Math.PI * 2);
    ctx.arc(player.x + player.width / 2 + 8, player.y + player.height / 2 - 5, 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(player.x + player.width / 2, player.y + player.height / 2 + 2, 8, 0, Math.PI);
    ctx.stroke();

    ctx.restore();
  };

  const drawStars = (ctx) => {
    gameDataRef.current.stars.forEach(star => {
      ctx.save();
      ctx.shadowColor = '#fbbf24';
      ctx.shadowBlur = 15;

      ctx.translate(star.x + star.width / 2, star.y + star.height / 2);
      ctx.rotate(Date.now() / 500);
      ctx.font = `${star.width}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('⭐', 0, 0);

      ctx.restore();
    });
  };

  const drawObstacles = (ctx) => {
    gameDataRef.current.obstacles.forEach(obstacle => {
      ctx.save();
      ctx.shadowColor = '#ef4444';
      ctx.shadowBlur = 15;

      ctx.translate(obstacle.x + obstacle.width / 2, obstacle.y + obstacle.height / 2);
      ctx.rotate(Date.now() / 300);
      ctx.font = `${obstacle.width}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('💣', 0, 0);

      ctx.restore();
    });
  };

  const drawParticles = (ctx) => {
    gameDataRef.current.particles.forEach(particle => {
      ctx.save();
      ctx.globalAlpha = particle.life;
      ctx.fillStyle = particle.color;
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    gameDataRef.current.stars = [];
    gameDataRef.current.obstacles = [];
    gameDataRef.current.particles = [];

    const canvas = canvasRef.current;
    if (canvas) {
      gameDataRef.current.player.x = canvas.width / 2;
      gameDataRef.current.player.y = canvas.height - 80;
    }
  };

  const endGame = () => {
    setGameState('gameOver');

    const { player, particles } = gameDataRef.current;
    particles.push(...createParticle(player.x + player.width / 2, player.y + player.height / 2, '#ef4444'));

    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('msaqeel-highscore', score.toString());
    }
  };

  return (
    <section id="demo" className="demo-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">جرب قوة ألعابنا</h2>
          <p className="section-subtitle">لعبة بسيطة تثبت مهاراتنا - اجمع النقاط وتجنب العوائق!</p>
        </div>

        <div className="game-container">
          <div className="game-wrapper">
            <canvas ref={canvasRef}></canvas>
            <div className="game-ui">
              <div className="game-stats">
                <div className="stat">
                  <span className="stat-label">النقاط</span>
                  <span className="stat-value">{score}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">أعلى نقاط</span>
                  <span className="stat-value">{highScore}</span>
                </div>
              </div>
              {gameState === 'gameOver' && (
                <div className="game-over">
                  <h3>انتهت اللعبة!</h3>
                  <p>نقاطك: <span className="final-score">{score}</span></p>
                  <button className="btn btn-primary" onClick={startGame}>العب مرة أخرى</button>
                </div>
              )}
              {gameState === 'start' && (
                <div className="game-start">
                  <h3>لعبة مصاقيل التجريبية</h3>
                  <p>استخدم الأسهم أو اسحب للتحرك</p>
                  <p>اجمع النجوم 🌟 وتجنب العوائق 💣</p>
                  <button className="btn btn-primary" onClick={startGame}>ابدأ اللعب</button>
                </div>
              )}
            </div>
            <div className="brand-placement">
              <span className="brand-tag">برعاية: علامتك التجارية</span>
            </div>
          </div>
          <div className="game-info">
            <h3>ألعاب تفاعلية بجودة عالية</h3>
            <ul className="features-list">
              <li>
                <span className="feature-icon">✨</span>
                <span>تجربة لعب سلسة وممتعة</span>
              </li>
              <li>
                <span className="feature-icon">📱</span>
                <span>متوافقة مع جميع الأجهزة</span>
              </li>
              <li>
                <span className="feature-icon">🎨</span>
                <span>تصميم جذاب وعصري</span>
              </li>
              <li>
                <span className="feature-icon">🔗</span>
                <span>دمج سلس للعلامات التجارية</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GameDemo;
